import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useCartStore } from '../store/cartStore';
import { toast } from 'react-toastify';
import Button from '../components/Button';
import GuestContactForm from '../components/GuestContactForm';
import { formatDateInTimezone, getBrowserTimezone } from '../utils/timezone';
import { fetchGroupedCart } from '../services/cart/cartService';
import { GroupedCart, CartItem } from '../services/cart/cartTypes';
import { api } from '../lib/api';
import {
  GuestContact,
  getGuestContact,
  setGuestContact,
  isGuestCheckoutActive,
} from '../services/checkout/guestCheckoutSession';

const CartPage: React.FC = () => {
  const auth = useAuth();
  const { cart, isLoading, validationErrors, fetchCart, removeItem, transferAnonymousCart } =
    useCartStore();
  const navigate = useNavigate();
  const [previousItemCount, setPreviousItemCount] = useState<number | null>(null);
  const [groupedCart, setGroupedCart] = useState<GroupedCart | null>(null);
  const [groupedCartFailed, setGroupedCartFailed] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  // A guest who has already submitted contact info (see GuestContactForm
  // below) has a real server-side cart, keyed by the guestCartId cookie, so
  // from here on they are treated the same as an authenticated caller for
  // grouped-cart/checkout purposes.
  const [guestCheckoutActive, setGuestCheckoutActive] = useState(isGuestCheckoutActive());
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [startingGuestCheckout, setStartingGuestCheckout] = useState(false);

  const loadGroupedCart = useCallback(async () => {
    try {
      const grouped = await fetchGroupedCart();
      setGroupedCart(grouped);
      setGroupedCartFailed(false);
    } catch (error) {
      // Without the grouping there are no checkout buttons at all, so failing
      // quietly leaves the page looking like the cart cannot be paid for
      console.error('Failed to load grouped cart:', error);
      setGroupedCartFailed(true);
    }
  }, []);

  useEffect(() => {
    const loadCart = async () => {
      if (auth.isLoading) {
        return;
      }

      await fetchCart();

      if (auth.isAuthenticated || guestCheckoutActive) {
        await loadGroupedCart();
      } else {
        // A guest who has not started checkout yet cannot usefully call the
        // grouped endpoint: their items still live in sessionStorage, not a
        // server-side cart.
        setGroupedCart(null);
        setGroupedCartFailed(false);
      }
    };
    void loadCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, auth.isLoading, guestCheckoutActive]);

  // Detect when items are auto-removed
  useEffect(() => {
    if (cart && previousItemCount !== null && cart.items.length < previousItemCount) {
      const removedCount = previousItemCount - cart.items.length;
      toast.info(`${removedCount} invalid item${removedCount > 1 ? 's' : ''} removed from cart (listing may have been updated)`);
    }
    if (cart) {
      setPreviousItemCount(cart.items.length);
    }
  }, [cart, previousItemCount]);

  const handleRemove = async (index: number) => {
    try {
      await removeItem(index);
      toast.success('Item removed from cart');
      if (auth.isAuthenticated || guestCheckoutActive) {
        await loadGroupedCart();
      }
    } catch {
      toast.error('Failed to remove item');
    }
  };

  const handlePlatformCheckout = () => {
    if (validationErrors.length > 0) {
      toast.error('Please resolve cart errors before checkout');
      return;
    }
    navigate('/checkout');
  };

  const handleStripeCheckout = async (ownerId: string) => {
    if (validationErrors.length > 0) {
      toast.error('Please resolve cart errors before checkout');
      return;
    }

    setCheckoutLoading(ownerId);
    try {
      const guestContact = auth.isAuthenticated ? undefined : getGuestContact() || undefined;
      const response = await api.post<{ sessionId: string; url: string }>(
        '/payments/stripe/create-session',
        guestContact ? { ownerId, guestContact } : { ownerId }
      );
      // Redirect to Stripe Checkout
      window.location.href = response.url;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      toast.error('Failed to start checkout. Please try again.');
      setCheckoutLoading(null);
    }
  };

  const handleLoginToCheckout = () => {
    void auth.signinRedirect();
  };

  const handleGuestContactSubmit = async (contact: GuestContact) => {
    setStartingGuestCheckout(true);
    try {
      // Persisting the contact first is what flips cartStore's usesServerCart()
      // to true, so the transfer below lands items in the server-side guest
      // cart instead of re-writing sessionStorage.
      setGuestContact(contact);
      const result = await transferAnonymousCart();
      if (result.failed > 0) {
        toast.error(`Failed to add ${result.failed} item(s) to your guest cart`);
      }
      setShowGuestForm(false);
      setGuestCheckoutActive(true);
    } catch (err) {
      console.error('Failed to start guest checkout:', err);
      toast.error('Failed to start guest checkout. Please try again.');
    } finally {
      setStartingGuestCheckout(false);
    }
  };

  const getGroupTotal = (items: CartItem[]) => {
    return items.reduce((sum, item) => sum + item.price, 0);
  };

  if (auth.isLoading || isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  const hasItems = cart && cart.items.length > 0;
  const ownerIds = groupedCart ? Object.keys(groupedCart.owners) : [];
  // Only true before a guest has committed to checkout — once they submit
  // the contact form below, they get the same grouped/checkout view as an
  // authenticated buyer.
  const isGuest = !auth.isAuthenticated && !guestCheckoutActive;
  const guestContact = guestCheckoutActive ? getGuestContact() : null;

  const renderCartItemCard = (item: CartItem, cartIndex: number) => {
    const hasError = validationErrors.some(e => e.index === cartIndex);
    const listingTitle = item.listingId?.title || 'Rental Item';
    // A booking's start/end instant means whatever it means in the listing's
    // timezone, not the buyer's browser timezone.
    const timezone = item.listingId?.timezone || getBrowserTimezone();

    return (
      <div
        key={cartIndex}
        className={`bg-white rounded-lg shadow-sm p-6 ${hasError ? 'border-2 border-red-300' : ''}`}
      >
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {listingTitle}
            </h3>
            <div className="space-y-1 text-gray-600 text-sm">
              <p>
                <span className="font-medium">Start:</span>{' '}
                {formatDateInTimezone(new Date(item.startDateTime), timezone)}
              </p>
              <p>
                <span className="font-medium">End:</span>{' '}
                {formatDateInTimezone(new Date(item.endDateTime), timezone)}
              </p>
              <p>
                <span className="font-medium">Price:</span> ${item.price.toFixed(2)}
              </p>
            </div>
            {hasError && (
              <div className="mt-2 text-red-600 text-sm font-medium">
                ⚠️ {validationErrors.find(e => e.index === cartIndex)?.error}
              </div>
            )}
          </div>
          <Button
            size="sm"
            variant="danger"
            onClick={() => handleRemove(cartIndex)}
          >
            Remove
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Shopping Cart</h1>

      {!hasItems ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <p className="text-gray-600 mb-4">Your cart is empty</p>
          <Link to="/">
            <Button>Browse Listings</Button>
          </Link>
        </div>
      ) : isGuest ? (
        <>
          <div className="space-y-4 mb-4">
            {cart!.items.map((item, index) => renderCartItemCard(item, index))}
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg font-bold text-gray-900">Total:</span>
              <span className="text-xl font-bold text-primary-600">
                ${getGroupTotal(cart!.items).toFixed(2)}
              </span>
            </div>
            {showGuestForm ? (
              <GuestContactForm
                onSubmit={handleGuestContactSubmit}
                isSubmitting={startingGuestCheckout}
                submitLabel="Continue to Payment"
              />
            ) : (
              <>
                <Button
                  onClick={() => setShowGuestForm(true)}
                  className="w-full"
                  size="lg"
                >
                  Checkout as Guest
                </Button>
                <Button
                  onClick={handleLoginToCheckout}
                  variant="secondary"
                  className="w-full mt-3"
                  size="lg"
                >
                  Log in to checkout
                </Button>
                <p className="text-gray-500 text-sm mt-3 text-center">
                  Check out as a guest and link this order to an account
                  afterward, or log in first for faster checkout next time.
                </p>
              </>
            )}
          </div>
        </>
      ) : (
        <>
          {guestContact && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 text-sm text-gray-700">
              Checking out as a guest ({guestContact.email}).
            </div>
          )}

          {validationErrors.length > 0 && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <h3 className="text-red-800 font-semibold mb-2">Cart Validation Errors</h3>
              <ul className="list-disc list-inside text-red-700 text-sm">
                {validationErrors.map((error, idx) => (
                  <li key={idx}>
                    Item {error.index + 1}: {error.error}
                  </li>
                ))}
              </ul>
              <p className="text-red-600 text-sm mt-2">
                Please remove invalid items before proceeding to checkout
              </p>
            </div>
          )}

          {groupedCartFailed && (
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
              <h3 className="text-yellow-800 font-semibold mb-1">
                Checkout options could not be loaded
              </h3>
              <p className="text-yellow-700 text-sm mb-3">
                Your items are still here. We just could not work out who to pay, so the
                checkout buttons are hidden.
              </p>
              <Button variant="secondary" onClick={() => void loadGroupedCart()}>
                Try Again
              </Button>
            </div>
          )}

          {groupedCart && groupedCart.platform.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm mr-3">
                  Platform Listings
                </span>
                <span className="text-gray-500 text-base">(PayPal Checkout)</span>
              </h2>
              
              <div className="space-y-4 mb-4">
                {groupedCart.platform.map((item, index) => {
                  const cartIndex = cart!.items.findIndex(ci => 
                    ci.listingId._id === item.listingId._id && 
                    ci.startDateTime === item.startDateTime
                  );
                  
                  return (
                    <React.Fragment key={index}>
                      {renderCartItemCard(item, cartIndex)}
                    </React.Fragment>
                  );
                })}
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-bold text-gray-900">Subtotal:</span>
                  <span className="text-xl font-bold text-primary-600">
                    ${getGroupTotal(groupedCart.platform).toFixed(2)}
                  </span>
                </div>
                <Button 
                  onClick={handlePlatformCheckout} 
                  className="w-full" 
                  size="lg"
                  disabled={validationErrors.length > 0}
                >
                  Checkout
                </Button>
              </div>
            </div>
          )}

          {ownerIds.map(ownerId => {
            const ownerGroup = groupedCart!.owners[ownerId];
            return (
              <div key={ownerId} className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm mr-3">
                    {ownerGroup.ownerName}
                  </span>
                  <span className="text-gray-500 text-base">(Stripe Checkout)</span>
                </h2>
                
                <div className="space-y-4 mb-4">
                  {ownerGroup.items.map((item, index) => {
                    const cartIndex = cart!.items.findIndex(ci => 
                      ci.listingId._id === item.listingId._id && 
                      ci.startDateTime === item.startDateTime
                    );
                    
                    return (
                      <React.Fragment key={index}>
                        {renderCartItemCard(item, cartIndex)}
                      </React.Fragment>
                    );
                  })}
                </div>

                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg font-bold text-gray-900">Subtotal:</span>
                    <span className="text-xl font-bold text-primary-600">
                      ${getGroupTotal(ownerGroup.items).toFixed(2)}
                    </span>
                  </div>
                  <Button 
                    onClick={() => handleStripeCheckout(ownerId)} 
                    className="w-full" 
                    size="lg"
                    disabled={validationErrors.length > 0 || checkoutLoading === ownerId}
                    isLoading={checkoutLoading === ownerId}
                  >
                    {checkoutLoading === ownerId ? 'Redirecting...' : 'Checkout with Stripe'}
                  </Button>
                </div>
              </div>
            );
          })}

          {hasItems && (
            <div className="bg-gray-50 border-t-4 border-gray-300 rounded-lg p-6 mt-6">
              <p className="text-gray-700 text-sm">
                <strong>Note:</strong> Items from different sellers require separate checkouts. 
                Complete each checkout to purchase all items in your cart.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CartPage;
